/**
 * Module catalogue — *what a section runs*, as opposed to *what a user may do*.
 *
 * Two orthogonal questions decide whether a screen exists for somebody (docs/03 §2a):
 *
 *   1. **Does this section run the module?**  → configuration, `Section.enabledModules`
 *   2. **Does this user hold the permission?** → authorisation, the permission catalogue
 *
 * They are not interchangeable. A permission is a grant and can only *widen* reach; a
 * module is a narrowing of the section it is configured on, and it is never a security
 * control on its own — the permission check still runs, and still runs first. Turning a
 * module off must therefore never be the only thing standing between a caller and data.
 *
 * The reason this exists at all: `section_manager` holds every vertical's `*.manage`
 * grant, so before modules a Real Estate manager could create a Construction project
 * inside the Real Estate section. The permission catalogue cannot express "this section
 * has no BOQ" — that is a fact about the business line, not about the person.
 */

import { P } from './permissions.js';
import { SECTION_SLUGS } from './constants/index.js';

export const MODULE = {
  // group-level: not configurable per section, listed so navigation can key off them
  DASHBOARD: 'dashboard',
  ADMIN: 'admin',

  // core: every section's books need these, so they cannot be switched off
  ACCOUNTING: 'accounting',
  TREASURY: 'treasury',
  PARTIES: 'parties',
  REPORTS: 'reports',

  // optional shared modules
  INVOICES: 'invoices',
  PAYMENTS: 'payments',
  EXPENSES: 'expenses',
  PAYROLL: 'payroll',
  COMMISSIONS: 'commissions',
  PROFITS: 'profits',

  // verticals — each one belongs to exactly one business line
  PROPERTIES: 'properties',
  AGENCY: 'agency',
  BROKERS: 'brokers',
  /**
   * Real Estate's own trading register (docs/10 §4.2a).
   *
   * §4.2 left Real Estate deliberately thin and gave it *no* vertical module, because the
   * property register went to Construction. Buying and selling on the company's own
   * account is the one thing that section does which no shared module covers, so it gets
   * a module of its own rather than being bolted onto `properties` — that key now means
   * "Construction's register", and enabling it on Real Estate would hand that section a
   * property list §4.1 deliberately moved away from it.
   */
  TREATS: 'treats',
  /**
   * The Cleaner section's orders and their expenses (docs/10 §50).
   *
   * A new key does **not** reach sections that already exist — `reconcileModules()` never
   * adds one. That is harmless here: the only section allowed to run it is Cleaner, which is
   * created by the same seed that ships the key and so starts from `defaultModulesFor`.
   */
  CLEANING: 'cleaning',
};

/**
 * One entry per module.
 *
 * `scope`       'group'   — HQ-level, never in a section's list (admin, dashboard)
 *               'section' — configurable per section
 * `core`        cannot be disabled: the section's books do not work without it
 * `sections`    the only section slugs that may enable it; `null` means any section
 * `permissions` every permission that belongs to this module. Used to explain a user's
 *               effective access ("what can they actually do here?") and to assert the
 *               catalogue and the registry never drift apart.
 */
export const MODULES = [
  {
    key: MODULE.DASHBOARD,
    scope: 'group',
    core: true,
    sections: null,
    /*
     * `section.view` lives with the shell rather than with administration. It is the
     * floor for every section-scoped role — without it the section switcher has nothing
     * to list (docs/03 §5) — so filing it under `admin` would make a broker's access
     * panel claim they administer the group, which is the opposite of true.
     *
     * `currency.view` is filed here for exactly the same reason. Reading the currency
     * list is not administration — it is what tells a form how many decimals an amount
     * has and what code to print beside it, so every money-touching role holds it. The
     * administrative half, `currency.manage`, is filed under `admin` below.
     */
    permissions: [P.SECTION_VIEW, P.CURRENCY_VIEW],
  },
  {
    key: MODULE.ADMIN,
    scope: 'group',
    core: true,
    sections: null,
    permissions: [
      P.USER_VIEW,
      P.USER_CREATE,
      P.USER_UPDATE,
      P.USER_SUSPEND,
      P.USER_ASSIGN_SECTION,
      P.ROLE_VIEW,
      P.ROLE_CREATE,
      P.ROLE_UPDATE,
      P.ROLE_DELETE,
      P.SECTION_UPDATE,
      P.SECTION_SETTINGS,
      P.SETTINGS_VIEW,
      P.SETTINGS_UPDATE,
      P.CURRENCY_MANAGE,
      P.AUDIT_VIEW,
    ],
  },
  {
    key: MODULE.ACCOUNTING,
    scope: 'section',
    core: true,
    sections: null,
    permissions: [
      P.ACCOUNT_VIEW,
      P.ACCOUNT_CREATE,
      P.ACCOUNT_UPDATE,
      P.JOURNAL_VIEW,
      P.JOURNAL_CREATE,
      P.JOURNAL_POST,
      P.JOURNAL_REVERSE,
      P.PERIOD_VIEW,
      P.PERIOD_LOCK,
      P.PERIOD_CLOSE,
      P.PERIOD_REOPEN,
    ],
  },
  {
    key: MODULE.TREASURY,
    scope: 'section',
    core: true,
    sections: null,
    permissions: [
      P.SAFE_VIEW,
      P.SAFE_CREATE,
      P.SAFE_UPDATE,
      P.SAFE_VIEW_BALANCE,
      P.SAFE_ADJUST,
      P.SAFE_RECON_VIEW,
      P.SAFE_RECON_CREATE,
      P.SAFE_RECON_APPROVE,
    ],
  },
  {
    key: MODULE.PARTIES,
    scope: 'section',
    core: true,
    sections: null,
    permissions: [
      P.PARTY_VIEW,
      P.PARTY_CREATE,
      P.PARTY_UPDATE,
      P.PARTY_DELETE,
      /**
       * **Real Estate tenancies live here, not in a vertical of their own** (docs/10 §4.2c).
       *
       * A tenancy is the agreement behind a row on the Tenants register, and that register
       * is already gated on this module — so the terms and the list they describe are
       * switched on and off by one fact, which is what they are.
       *
       * A `MODULE.TENANCIES` scoped to Real Estate was the tidier-looking alternative and it
       * has a trap in it: `reconcileModules()` renames and drops stored keys and never
       * **adds** one, and `defaultModulesFor()` only applies to a section that has never been
       * configured. So a brand-new module key is absent from every already-seeded section,
       * and the menu item silently does not appear — the failure §9b describes, arriving as
       * "the feature did not ship" rather than as an error. `parties` is core and therefore
       * present everywhere, today and on every install that already exists.
       *
       * The section slug is what keeps this Real Estate's: the nav group carries
       * `sections: [realestate]`, and no other section links to the screens.
       */
      P.RE_TENANCY_VIEW,
      P.RE_TENANCY_MANAGE,
    ],
  },
  {
    key: MODULE.REPORTS,
    scope: 'section',
    core: true,
    sections: null,
    permissions: [
      P.REPORT_VIEW,
      P.REPORT_EXPORT,
      P.REPORT_CONSOLIDATED_VIEW,
      P.ATTACHMENT_VIEW,
      P.ATTACHMENT_UPLOAD,
      P.ATTACHMENT_DELETE,
    ],
  },
  {
    key: MODULE.INVOICES,
    scope: 'section',
    core: false,
    sections: null,
    permissions: [
      P.INVOICE_VIEW,
      P.INVOICE_CREATE,
      P.INVOICE_UPDATE,
      P.INVOICE_SUBMIT,
      P.INVOICE_APPROVE,
      P.INVOICE_POST,
      P.INVOICE_VOID,
      P.INVOICE_DELETE,
    ],
  },
  {
    key: MODULE.PAYMENTS,
    scope: 'section',
    core: false,
    sections: null,
    permissions: [P.PAYMENT_VIEW, P.PAYMENT_CREATE, P.PAYMENT_APPROVE, P.PAYMENT_POST, P.PAYMENT_VOID],
  },
  {
    key: MODULE.EXPENSES,
    scope: 'section',
    core: false,
    sections: null,
    permissions: [
      P.EXPENSE_VIEW,
      P.EXPENSE_CREATE,
      P.EXPENSE_SUBMIT,
      P.EXPENSE_APPROVE,
      P.EXPENSE_POST,
      P.EXPENSE_VOID,
    ],
  },
  {
    key: MODULE.PAYROLL,
    scope: 'section',
    core: false,
    sections: null,
    permissions: [P.PAYROLL_VIEW, P.PAYROLL_CREATE, P.PAYROLL_APPROVE, P.PAYROLL_POST, P.PAYROLL_PAY],
  },
  {
    key: MODULE.COMMISSIONS,
    scope: 'section',
    core: false,
    sections: null,
    permissions: [
      P.COMMISSION_PLAN_VIEW,
      P.COMMISSION_PLAN_CREATE,
      P.COMMISSION_PLAN_UPDATE,
      P.COMMISSION_PLAN_DEACTIVATE,
      P.COMMISSION_VIEW,
      P.COMMISSION_APPROVE,
      P.COMMISSION_CANCEL,
      P.COMMISSION_PAYOUT,
    ],
  },
  {
    key: MODULE.PROFITS,
    scope: 'section',
    core: false,
    sections: null,
    permissions: [P.PROFIT_VIEW, P.PROFIT_COMPUTE, P.PROFIT_DISTRIBUTE, P.PROFIT_APPROVE],
  },
  {
    key: MODULE.PROPERTIES,
    scope: 'section',
    core: false,
    /*
     * Construction, not Real Estate (docs/10 §4.1). The section that builds and sells
     * properties is the one that needs the properties module; Real Estate is now thin —
     * invoices, expenses and reports — and enables nothing here (§4.2).
     */
    sections: [SECTION_SLUGS.CONSTRUCTION],
    permissions: [
      P.PROP_PROPERTY_VIEW,
      P.PROP_PROPERTY_MANAGE,
      P.PROP_CONTRACT_VIEW,
      P.PROP_CONTRACT_MANAGE,
      P.PROP_OWNER_VIEW,
      P.PROP_OWNER_MANAGE,
    ],
  },
  {
    key: MODULE.AGENCY,
    scope: 'section',
    core: false,
    sections: [SECTION_SLUGS.AGENCY],
    permissions: [P.AG_CONTRACT_VIEW, P.AG_CONTRACT_MANAGE, P.AG_CAMPAIGN_VIEW, P.AG_CAMPAIGN_MANAGE],
  },
  {
    key: MODULE.BROKERS,
    scope: 'section',
    core: false,
    sections: [SECTION_SLUGS.BROKERS],
    permissions: [
      P.BR_PROFILE_VIEW,
      P.BR_PROFILE_MANAGE,
      P.BR_DEAL_VIEW,
      P.BR_DEAL_MANAGE,
      P.BR_DEAL_VIEW_MARGIN,
      P.BR_PAYOUT_MANAGE,
      P.BR_CAPITAL_VIEW,
      P.BR_CAPITAL_MANAGE,
    ],
  },
  {
    /**
     * Real Estate treats — the company buying a property and selling it on (§4.2a).
     *
     * `sections` names Real Estate alone because that is the business line that asked for
     * it. Construction sells property too, but it sells what it *built*, through sale
     * contracts and instalments; this is buying at one price and selling at another,
     * which is a different trade. Widening it later is one slug in this array.
     *
     * Not `core`: a Real Estate office that only manages rent turns this off and loses a
     * menu item, not its books.
     */
    key: MODULE.TREATS,
    scope: 'section',
    core: false,
    sections: [SECTION_SLUGS.REAL_ESTATE],
    permissions: [P.RE_TREAT_VIEW, P.RE_TREAT_MANAGE],
  },
  {
    /* Cleaner's vertical. Not `core`: turning it off hides the register, not the books. */
    key: MODULE.CLEANING,
    scope: 'section',
    core: false,
    sections: [SECTION_SLUGS.CLEANER],
    permissions: [P.CL_ORDER_VIEW, P.CL_ORDER_MANAGE],
  },
];

export const ALL_MODULE_KEYS = Object.freeze(MODULES.map((m) => m.key));

const BY_KEY = new Map(MODULES.map((m) => [m.key, m]));

export const getModule = (key) => BY_KEY.get(key) || null;
export const isValidModule = (key) => BY_KEY.has(key);

/** Modules that live on a section at all — `admin` and `dashboard` never appear in a list. */
export const SECTION_MODULE_KEYS = Object.freeze(
  MODULES.filter((m) => m.scope === 'section').map((m) => m.key),
);

/** Modules a section's books cannot run without, so the toggles cannot remove them. */
export const CORE_MODULE_KEYS = Object.freeze(
  MODULES.filter((m) => m.scope === 'section' && m.core).map((m) => m.key),
);

/**
 * Which modules a section is *allowed* to enable.
 *
 * HQ gets everything: it is the group's own books and the place a consolidated view is
 * assembled, so a module it cannot see is a hole in the group picture rather than a
 * simplification.
 */
export const availableModulesFor = (slug) =>
  MODULES.filter(
    (m) => m.scope === 'section' && (!m.sections || slug === SECTION_SLUGS.HQ || m.sections.includes(slug)),
  ).map((m) => m.key);

/**
 * What a freshly seeded section runs: every shared module, plus its own vertical.
 *
 * Deliberately permissive for the shared modules — a group that does not invoice out of
 * one of its sections turns that off explicitly, which is a decision somebody made and
 * the audit log records. Shipping a section with invoicing already missing is a support
 * ticket that reads as a bug.
 */
export const defaultModulesFor = (slug) => availableModulesFor(slug);

/**
 * Keys that used to exist, and what — if anything — they became.
 *
 * `Section.enabledModules` is enum-validated, so a row still holding a retired key cannot be
 * saved *at all*: the whole document is refused, and the failure surfaces somewhere unrelated
 * — the chart seed's backfill, in the case that produced this map. A restructure that renames
 * a module therefore has to say so somewhere the seed can read.
 *
 * A key mapping to a successor keeps the capability; one mapping to nothing was retired
 * outright (docs/10 §7). The successor is still checked against the slug, so it only lands
 * where the catalogue allows it: Real Estate stored `realestate` and does **not** get
 * Properties back, because §4.2 moved that register to Construction with the module.
 */
export const RENAMED_MODULES = Object.freeze({
  /** §4.1 — the property register moved to Construction and the module was renamed with it. */
  realestate: MODULE.PROPERTIES,
  /** §4.1 / §7.3 — the BOQ machinery is retired; Construction's vertical is now Properties. */
  construction: MODULE.PROPERTIES,
  /** §7.1 — budgets are gone and nothing replaced them; the brokers model was inverted. */
  budgets: null,
});

/**
 * Bring a stored module list up to the current catalogue: rename what moved, drop what is
 * gone, and keep only what this slug is allowed to run.
 *
 * Only the keys already stored are considered, so a module the operator deliberately turned
 * off stays off — the seed's job is to make a row *savable* and current, not to overrule the
 * person who configured it.
 */
export const reconcileModules = (keys = [], slug) => {
  const allowed = new Set(availableModulesFor(slug));
  const out = [];

  for (const key of keys) {
    const current = Object.hasOwn(RENAMED_MODULES, key) ? RENAMED_MODULES[key] : key;
    if (current && allowed.has(current) && !out.includes(current)) out.push(current);
  }

  return out;
};

/**
 * Is `key` live for this section?
 *
 * A core or group-scoped module is always live; anything else must be in the list. A
 * section with **no** list at all (an older row that predates this field) is read as
 * "everything its slug allows" rather than "nothing" — the alternative would take a
 * working install offline on deploy, and a missing configuration value is not a
 * statement that the business stopped doing the work.
 */
export const isModuleEnabled = (section, key) => {
  const mod = BY_KEY.get(key);
  if (!mod) return false;
  if (mod.scope === 'group' || mod.core) return true;
  if (mod.sections && section?.slug && section.slug !== SECTION_SLUGS.HQ) {
    if (!mod.sections.includes(section.slug)) return false;
  }

  const list = section?.enabledModules;
  if (!Array.isArray(list) || list.length === 0) return availableModulesFor(section?.slug).includes(key);
  return list.includes(key);
};

/** The full live set for a section, core modules included. */
export const enabledModulesFor = (section) =>
  availableModulesFor(section?.slug).filter((key) => isModuleEnabled(section, key));

/** Reverse index: which module owns a permission. Used to explain effective access. */
const MODULE_BY_PERMISSION = new Map();
for (const mod of MODULES) {
  for (const permission of mod.permissions) MODULE_BY_PERMISSION.set(permission, mod.key);
}

export const moduleForPermission = (permission) => MODULE_BY_PERMISSION.get(permission) || null;

/**
 * Group a flat permission list by module — the shape both the access explainer and the
 * "what will this user see?" panel render.
 *
 * @param {string[]} permissions
 * @returns {{module: string, permissions: string[]}[]} in registry order
 */
export const groupPermissionsByModule = (permissions = []) => {
  const held = new Set(permissions);
  return MODULES.map((mod) => ({
    module: mod.key,
    permissions: mod.permissions.filter((p) => held.has(p)),
  })).filter((row) => row.permissions.length > 0);
};

export default MODULES;
